import { TLShapeId, useEditor } from "@tldraw/tldraw";
import { useCallback, useEffect, useState } from "react";
import { useStore } from "../store";
import { StoreType } from "../store/types";
import { shapesAddressBox } from "../pages/flyer/builder/components/SideBar/Aside/components/Settings/snapshots/addressBox";
import { shapesAddressBoxEddm } from "../pages/flyer/builder/components/SideBar/Aside/components/Settings/snapshots/addressBoxEddm";
import { CustomAddressBoxShape } from "pages/flyer/builder/drawer/state/newShapes/CustomAddressBoxUtil";

export type AddressBoxType = "ADM" | "EDDM";
const ADDRESS_BOX_ID = "shape:addressBox" as TLShapeId;
const BOUNDARY_IDS: TLShapeId[] = ["shape:frontBorderDashLine" as TLShapeId, "shape:backBorderDashLine" as TLShapeId];

export const useAddressBox = () => {
  const editor = useEditor();
  const { isAddressBoxVisible, setIsAddressBoxVisible } = useStore();
  const [visibleType, setVisibleType] = useState<AddressBoxType | null>(null);
  const [pendingClampAfterDrag, setPendingClampAfterDrag] = useState(false);

  const clampAddressBoxIntoBounds = useCallback(() => {
    const addressBox = editor.getShape(ADDRESS_BOX_ID) as CustomAddressBoxShape | null;
    if (!addressBox) {
      return;
    }

    // Use tldraw's built-in page bounds — this gives the REAL visual bounding box
    // already accounting for rotation, so we never need to manually swap w/h.
    const pageBounds = editor.getShapePageBounds(ADDRESS_BOX_ID);
    if (!pageBounds) {
      return;
    }

    const boundsCandidates = BOUNDARY_IDS.map((id) => editor.getShape(id)).filter(Boolean) as any[];
    if (!boundsCandidates.length) {
      return;
    }

    // Visual center from the REAL page bounds (rotation-correct)
    const visualCX = pageBounds.x + pageBounds.w / 2;
    const visualCY = pageBounds.y + pageBounds.h / 2;
    const visualW = pageBounds.w;
    const visualH = pageBounds.h;

    const clampValue = (value: number, min: number, max: number) => {
      if (Number.isNaN(min) || Number.isNaN(max)) {
        return value;
      }
      if (min > max) {
        // Shape doesn't fit — center it
        return (min + max) / 2;
      }
      return Math.min(Math.max(value, min), max);
    };

    let bestDx = 0;
    let bestDy = 0;
    let shortestDistance = Number.POSITIVE_INFINITY;
    let needsClamp = false;

    for (const boundsShape of boundsCandidates) {
      const boundsWidth = boundsShape?.props?.w ?? boundsShape?.props?.width ?? 0;
      const boundsHeight = boundsShape?.props?.h ?? boundsShape?.props?.height ?? 0;

      if (!boundsWidth || !boundsHeight) {
        continue;
      }

      // Clamp the visual CENTER so the visual extent stays within bounds
      const minCX = boundsShape.x + visualW / 2;
      const minCY = boundsShape.y + visualH / 2;
      const maxCX = boundsShape.x + boundsWidth - visualW / 2;
      const maxCY = boundsShape.y + boundsHeight - visualH / 2;

      const withinX = visualCX >= minCX && visualCX <= maxCX;
      const withinY = visualCY >= minCY && visualCY <= maxCY;

      if (withinX && withinY) {
        // Already inside this boundary — no clamp needed
        needsClamp = false;
        shortestDistance = 0;
        break;
      }

      const clampedCX = clampValue(visualCX, minCX, maxCX);
      const clampedCY = clampValue(visualCY, minCY, maxCY);
      const distance = Math.hypot(clampedCX - visualCX, clampedCY - visualCY);

      if (distance < shortestDistance) {
        shortestDistance = distance;
        // KEY: compute the DELTA the visual center needs to move.
        // Moving shape.x/y by delta ALWAYS moves the visual center by the same delta,
        // regardless of rotation. This is what makes this approach rotation-proof.
        bestDx = clampedCX - visualCX;
        bestDy = clampedCY - visualCY;
        needsClamp = true;
      }
    }

    if (needsClamp && (Math.abs(bestDx) > 0.5 || Math.abs(bestDy) > 0.5)) {
      editor.updateShape({
        id: ADDRESS_BOX_ID,
        type: "customAddressBox",
        x: addressBox.x + bestDx,
        y: addressBox.y + bestDy,
      });
    }
  }, [editor]);

  useEffect(() => {
    const addressBox = editor.getShape(ADDRESS_BOX_ID) as CustomAddressBoxShape | null;
    const isVisible = Boolean(addressBox && addressBox.type === "customAddressBox");

    if (isAddressBoxVisible !== isVisible) {
      setIsAddressBoxVisible(isVisible);
    }

    if (isVisible && addressBox) {
      setVisibleType(addressBox.props.variant === "EDDM" ? "EDDM" : "ADM");
    } else {
      setVisibleType(null);
    }
  }, [isAddressBoxVisible, editor, setIsAddressBoxVisible]);

  useEffect(() => {
    const unsubscribe = editor.store.listen(
      ({ changes }: any) => {
        if (changes?.added) {
          const addedShapes = Object.values(changes.added).filter(
            (record: any) => record.typeName === "shape"
          ) as CustomAddressBoxShape[];

          const unauthorizedAddressBoxes = addedShapes
            .filter((shape) => shape.type === "customAddressBox" && shape.id !== ADDRESS_BOX_ID)
            .map((shape) => shape.id as TLShapeId);

          if (unauthorizedAddressBoxes.length > 0) {
            editor.deleteShapes(unauthorizedAddressBoxes);
          }
        }

        if (changes?.removed && ADDRESS_BOX_ID in changes.removed) {
          setIsAddressBoxVisible(false);
          setVisibleType(null);
          return;
        }

        if (changes?.updated && ADDRESS_BOX_ID in changes.updated) {
          const isOrientationChanging = (useStore.getState() as StoreType).isOrientationChanging;
          if (isOrientationChanging) {
            // Skip clamping during orientation change — rotation logic handles positioning
          } else if (editor.inputs.isDragging) {
            setPendingClampAfterDrag(true);
          } else {
            clampAddressBoxIntoBounds();
          }
        }

        // When boundary shapes (canvas borders) change (e.g. orientation switch),
        // clamp the address box so it stays inside the back canvas.
        // Skip during orientation change — our rotation logic handles repositioning.
        if (changes?.updated) {
          const boundaryChanged = BOUNDARY_IDS.some((id) => id in changes.updated);
          const isOrientationChanging = (useStore.getState() as StoreType).isOrientationChanging;
          if (boundaryChanged && !(ADDRESS_BOX_ID in changes.updated) && !isOrientationChanging) {
            clampAddressBoxIntoBounds();
          }
        }
      },
      { source: "user", scope: "document" }
    );

    return () => {
      unsubscribe();
    };
  }, [editor, setIsAddressBoxVisible, setVisibleType, clampAddressBoxIntoBounds]);

  useEffect(() => {
    const handlePointerEvent = (info: any) => {
      if (info?.type !== "pointer") {
        return;
      }

      if (info.name === "pointer_up" || info.name === "pointer_cancel") {
        if (pendingClampAfterDrag) {
          setPendingClampAfterDrag(false);
          clampAddressBoxIntoBounds();
        }
      }
    };

    editor.on("event", handlePointerEvent);
    return () => {
      editor.off("event", handlePointerEvent);
    };
  }, [editor, pendingClampAfterDrag, clampAddressBoxIntoBounds]);

  const upsertAddressBox = useCallback(
    (type: AddressBoxType) => {
      // Get the current page bounds and center
      const pageBounds = editor.getCurrentPageBounds();
      if (pageBounds) {
        const centerX = (pageBounds.minX + pageBounds.maxX) / 2;
        const centerY = (pageBounds.minY + pageBounds.maxY) / 2;
        const displayWidth = pageBounds.width;
        const displayHeight = pageBounds.height;

        const isPortrait = editor.getCurrentPage()?.meta?.orientation === "portrait";

        // Use the address box type passed from the component
        const addressBoxFunction = type === "EDDM" ? shapesAddressBoxEddm : shapesAddressBox;

        // Create or update address box shapes
        addressBoxFunction(
          editor,
          isPortrait,
          centerX,
          centerY,
          displayWidth,
          displayHeight,
          "", // frontBgAssetId
          20, // safetyZonePx
          3, // bleedsZoneMM
          "", // logo
          "", // qrCode
          isAddressBoxVisible // existSnapshot - if true, update existing shapes
        );

        // Update the visible type
        setVisibleType(type);
        setIsAddressBoxVisible(true);
      }
    },
    [isAddressBoxVisible, editor, setIsAddressBoxVisible]
  );

  const hideAddressBox = useCallback(
    (_type: AddressBoxType) => {
      try {
        setIsAddressBoxVisible(false);
        setVisibleType(null);
        editor.deleteShapes([ADDRESS_BOX_ID]);
      } catch (error) {
        console.error(error);
      }
    },
    [setIsAddressBoxVisible, editor]
  );

  return {
    isAddressBoxVisible,
    visibleType,
    upsertAddressBox,
    hideAddressBox,
  };
};
